import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import toast from "components/ui/toast/Toast";
import { useGetBidDocumentUrl } from "lib/hooks/api/bid/bid_document/useGetBidDocumentUrl";
import { getBidDocumentUrlForExternalUser } from "lib/hooks/api/bid/public/getBidDocumentUrlForExternalUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { AiSourceDocumentType } from "lib/types/graphQLEnums";
import { FileViewerContextProvider } from "../../lib/core_components/FileViewer";
import { Flex } from "../../styles/utility-components";
import DocumentViewer from "./DocumentViewer";
import { ScorecardSource } from "./types";

type Props = {
  open: boolean;
  source: ScorecardSource;
  documentType: AiSourceDocumentType;
  bidId?: string;
  urlShareSignature?: string;
  onClose: () => void;
};

// TODO: Replace with CustomerDocumentDrawer once we migrate bid reports to use customer documents
export default function DocumentDrawer({
  open,
  source,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  documentType,
  bidId,
  urlShareSignature,
  onClose,
}: Props) {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  const { mutateAsync: getBidDocumentUrlMutation } = useGetBidDocumentUrl();

  useEffect(() => {
    const loadDocumentUrl = async () => {
      if (window.currentUser) {
        // TODO: Branch based on documentType
        const { getBidDocumentUrl } = await getBidDocumentUrlMutation({ id: source.documentId });
        setDocumentUrl(getBidDocumentUrl.documentUrl);
      } else {
        if (!bidId) throw new Error(`No bidId provided for document ${source.documentId}`);
        if (!urlShareSignature)
          throw new Error(`No signature found for document ${source.documentId}`);

        const resp = await getBidDocumentUrlForExternalUser(
          bidId,
          source.documentId,
          urlShareSignature,
        );
        setDocumentUrl(resp.documentUrl);
      }
    };

    loadDocumentUrl().catch((e) => {
      toast.error("Failed to load document");
      Sentry.captureException(e);
      onClose();
    });
  }, [bidId, getBidDocumentUrlMutation, onClose, source.documentId, urlShareSignature]);

  // TODO: this should be done in the backend
  let fileExtension = source.fileName?.split(".").pop() || "";
  if (["odt", "odp", "ods"].includes(fileExtension)) {
    fileExtension = "pdf";
  }
  return (
    <Drawer
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      width={1200}
    >
      <FileViewerContextProvider key={`${documentUrl}-${+open}`}>
        <PreviewWrapper>
          <Controls>
            <Button
              buttonType="secondary"
              title="Close"
              icon="chevronRightDouble"
              onClick={onClose}
            />
          </Controls>
          {documentUrl ? (
            <DocumentViewer
              documentUrl={documentUrl}
              fileExtension={fileExtension}
              source={source}
              open={open}
            />
          ) : (
            <Flex alignItems="center" justifyContent="center" width="100%">
              <UIcon icon="spinner" size={32} />
            </Flex>
          )}
        </PreviewWrapper>
      </FileViewerContextProvider>
    </Drawer>
  );
}

const PreviewWrapper = styled(Flex)({
  paddingRight: 16,
});

const Controls = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  alignItems: "center",
  borderRight: `1px solid ${theme.colors.sysBorderDecorative}`,
  height: "100vh",
  padding: 16,
}));
